import { type FieldError } from 'react-hook-form';
import { clsx } from 'clsx';
import { format, lightFormat } from "date-fns";
import { isValidDate } from "../helpers/utils.ts";

export function inputRadioClass(error: boolean|string|FieldError = false) {
	return clsx('border rounded-xl', (error ? 'error !border-red-400' : 'border-transparent'));
}

export function inputClass(error: boolean|string|FieldError = false) {
	return clsx('text-black', error && 'error !border-red-400');
}

export function getOfferIntervalName(offer): string {
	if (offer?.billingSchedule?.interval === 'P1W') {
		return 'week';
	}

	if (offer?.billingSchedule?.interval === 'P1M') {
		return 'month';
	}

	return '';
}

/*
	Number.isNaN(null) is false, so a null amount used to slip past the old guard
	and throw on .toLocaleString() — which took the whole plan list down with it.
	Anything that is not a usable number is treated as zero here so that a single
	unpriced record can never blank the page.

	Callers should still avoid rendering a price they do not have: showing £0.00
	for a product with no price is wrong, just less wrong than a white screen.
 */
function toAmount(amount: unknown): number {
	return typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
}

export function formatPrice(amount: number, currency: string) {
	const value = toAmount(amount);

	if (currency === 'GBP') {
		return value.toLocaleString('en-GB', {
			style: 'currency',
			currency: currency,
		}).replace('£', '');
	}
	return value + ' ' + currency;
}

export function formatPriceWithCurrency(amount: number, currency: string) {
	const value = toAmount(amount);

	if (currency === 'GBP') {
		return value.toLocaleString('en-GB', {
			style: 'currency',
			currency: currency,
		});
	}
	return value + ' ' + currency;
}

/*
	An offer with no billing schedule serialises billingSchedule as an empty array
	rather than an object or null, so check for the amount itself rather than for
	the property's presence.

	This distinguishes recurring plans from one-off ones ("Payment in full",
	"Single rate"). It must NOT be used to decide whether to show an offer at all —
	the one-off products are legitimate and appear on the live checkout.
 */
export function hasBillingSchedule(offer): boolean {
	return typeof offer?.billingSchedule?.amount === 'number';
}

/*
	The headline price for an offer, recurring or not.

	Neither field alone is sufficient, and getting this wrong is expensive in
	both directions:

	  priceTI  billingSchedule  example                        correct price
	  -------  ---------------  -----------------------------  -------------
	     4995             4995  Gym Off-Peak                          priceTI
	     7000             none  1 month paid upfront                  priceTI
	     2000             none  Pay as you go                         priceTI
	        0             6495  BJJ 2 classes/week (Monthly)  billingSchedule

	Pricing from billingSchedule alone hides the two one-off products entirely.
	Pricing from priceTI alone renders the Jiu-Jitsu and Muay Thai monthly
	plans as £0.00 — i.e. free memberships. So: prefer priceTI, fall back to
	the billing schedule when it is zero or absent.
 */
/*
	A subscription renews on a billing schedule. A regular product is bought once
	("Pay as you go", "1 month paid upfront") and never renews, so everything that
	describes an ongoing membership — automatic renewal, a pro rata first month,
	the word "plan" itself — is wrong for it.

	Delegates to hasBillingSchedule: the billing schedule is the distinction, this
	name just says what the distinction means.
 */
export function isSubscription(offer): boolean {
	// The checkout steps get the answer snapshotted onto the session when the plan
	// was chosen, so they do not re-derive it. The plan cards hold a raw showcase
	// offer with no such flag, and so do sessions predating the column — both fall
	// back to the billing schedule.
	if (typeof offer?.subscription === 'boolean') {
		return offer.subscription;
	}

	return hasBillingSchedule(offer);
}

/*
	The two checkout paths, decided by whether the chosen offer renews.

	The server is authoritative about which path a session is on: it snapshots
	offerSubscription when the plan is chosen and only skips the mandate for an
	offer positively known not to renew. The indicator mirrors that exact rule
	rather than isSubscription(), so a session predating the column (null) shows
	the four steps it will actually be walked through.
 */
const STEPS_SUBSCRIPTION = ["YOUR MEMBERSHIP", "YOUR DETAILS", "DIRECT DEBIT", "PAYMENT"];
const STEPS_ONE_OFF = ["YOUR MEMBERSHIP", "YOUR DETAILS", "PAYMENT"];

export function skipsDirectDebit(session): boolean {
	return session?.offerSubscription === false;
}

export function checkoutStepLabels(session): string[] {
	return skipsDirectDebit(session) ? STEPS_ONE_OFF : STEPS_SUBSCRIPTION;
}

/*
	Which indicator step the session's server-side step falls under, or null when
	the indicator has nothing to show: the plan list, the outcome screens, and a
	session that has not loaded yet.
 */
export function checkoutStepIndex(session): number|null {
	switch (session?.step) {
		case 2:
			return 0;
		case 3:
			return 1;
		case 4:
		case 5:
			return 2;
		case 6:
			return skipsDirectDebit(session) ? 2 : 3;
	}

	return null;
}

export type CheckoutStepStatus = 'complete' | 'current' | 'reached' | 'upcoming';

export type CheckoutStepState = Readonly<{
	label: string,
	targetStep: number,
	status: CheckoutStepStatus,
	navigable: boolean,
}>;

/*
	The per-step state the indicator renders from. Completion is driven by what the
	session has actually captured — start date, contact, mandate, payment — not by
	the step the customer is currently on, so navigating back does not un-complete
	the steps they already finished. A step is navigable when it has been reached
	(so you can move back and forth freely between reached steps), except the one
	you are already on. Plan selection and the SMS dialog are never targets.
*/
export function checkoutStepStates(session): CheckoutStepState[] {
	const labels = checkoutStepLabels(session);
	const subscription = !skipsDirectDebit(session);
	const currentIndex = checkoutStepIndex(session);

	// index -> the server step it maps to
	const targets = subscription ? [2, 3, 4, 6] : [2, 3, 6];

	// Payment is reached once the thing that precedes it exists: the mandate for a
	// subscription, or simply the contact for a one-off (which has no direct debit).
	const reachedPayment = subscription ? Boolean(session?.mandateId) : Boolean(session?.contactId);

	return labels.map((label, index) => {
		const target = targets[index];
		let complete = false;
		let reached = false;

		switch (target) {
			case 2: complete = reached = Boolean(session?.startDate); break;
			case 3: complete = reached = Boolean(session?.contactId); break;
			case 4: complete = Boolean(session?.mandateId); reached = Boolean(session?.contactId); break;
			case 6: complete = Boolean(session?.paid); reached = reachedPayment; break;
		}

		const current = index === currentIndex;
		const status: CheckoutStepStatus = current ? 'current' : complete ? 'complete' : reached ? 'reached' : 'upcoming';

		return { label, targetStep: target, status, navigable: reached && !current };
	});
}

export function getOfferPrice(offer) {
	if (typeof offer?.priceTI === 'number' && offer.priceTI > 0) {
		return offer.priceTI / 100;
	}
	if (hasBillingSchedule(offer)) {
		return offer.billingSchedule.amount / 100;
	}
	return null;
}

/* The recurring amount, or null for a one-off offer. */
export function getOfferRecurringPrice(offer) {
	if (!hasBillingSchedule(offer)) {
		return null;
	}
	return offer.billingSchedule.amount / 100;
}

/*
	registrationFeeTI is a scalar integer in pence (e.g. 3000 for £30), NOT an
	object with an .amount. Reading .amount off it yields undefined, which then
	divides to NaN and renders as £0.00 — and, because null is falsy, silently
	removes the joining fee from the summary total. See useSummaryCalculator.
 */
export function getOfferAdminFee(offer) {
	if (typeof offer?.registrationFeeTI !== 'number') {
		return null;
	}
	return offer.registrationFeeTI / 100;
}

/*
	BACS caps the account name at 18 characters. Verified against the API: 18 is
	accepted, 19 returns api.error.mandate.too-long-accountName.
 */
export const ACCOUNT_NAME_MAX_LENGTH = 18;

/*
	A starting point for the account name field when the member's full name does
	not fit.

	This is NOT the member's name — it is the name held on the bank account, and
	it has to be close enough to what the bank has or the collection can be
	rejected. So this only suggests; the customer can always overwrite it, and
	they are told why the limit exists.

	"Christopher Fairweather" (23) -> "C Fairweather" (13)
 */
export function suggestAccountName(givenName?: string, familyName?: string): string {
	const given = (givenName ?? '').trim();
	const family = (familyName ?? '').trim();

	const full = [given, family].filter(Boolean).join(' ');
	if (full.length <= ACCOUNT_NAME_MAX_LENGTH) {
		return full;
	}

	const initial = given.charAt(0);
	const abbreviated = [initial, family].filter(Boolean).join(' ');
	if (abbreviated.length <= ACCOUNT_NAME_MAX_LENGTH) {
		return abbreviated;
	}

	// Even the abbreviation is too long. Hand back something of the right length
	// rather than a value the API will reject, and let the customer correct it.
	return (abbreviated || full).slice(0, ACCOUNT_NAME_MAX_LENGTH).trim();
}

export function formatDate(date = new Date()) {
	if (isValidDate(date)) {
		return lightFormat(date, 'yyyy-MM-dd');
	}

	return null;
}

/*
	The start date is a plain CALENDAR DAY — the day the customer picked, the day sent to
	the API as yyyy-MM-dd (formatDate), and the day the pro-rata is billed from. The session
	stores it and serialises it back as a UTC-midnight ISO string ("2026-10-01T00:00:00Z").
	Parsing that with `new Date()` re-reads the instant in the browser's timezone, so in any
	zone behind UTC it lands on the day BEFORE — shifting the start, the pro-rata and the
	"Starts on" line by a day between screens. Pin it to the calendar day the string names,
	at LOCAL midnight, so every step (and the API) agree in every timezone. Falls back to a
	lenient parse for anything that is not the expected yyyy-MM-dd[...] shape.
*/
export function parseStartDate(value: string | Date | null | undefined): Date | null {
	if (value instanceof Date) {
		return isValidDate(value) ? value : null;
	}
	if (!value) {
		return null;
	}
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
	if (match) {
		return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
	}
	const parsed = new Date(value);
	return isValidDate(parsed) ? parsed : null;
}

// Human-friendly date for display, e.g. "1 September 2026". Kept separate from
// formatDate, which must stay yyyy-MM-dd because the API depends on that shape.
// Returns '' for a missing or invalid date rather than printing "Invalid Date"
// (or a stray "1970-01-01" when a null slips through as new Date(null)).
export function formatDisplayDate(date: Date | string | number | null | undefined): string {
	if (date === null || date === undefined || date === '') {
		return '';
	}

	const parsed = date instanceof Date ? date : new Date(date);

	return isValidDate(parsed) ? format(parsed, 'd MMMM yyyy') : '';
}

export function extractIdFromHydraId(hydraId: string) {
	//@id	"/demoapi/contacts/2974574"
	return hydraId.split('/').pop();
}

/*
	Descriptions come straight from the Resa showcase_product record, so their
	shape is whatever was typed into the vendor's admin — typically one feature
	per line prefixed with an en dash.

	A bullet is a dash followed by a space. The space is what carries the meaning:
	"– Access: ..." is a feature, while "–1-Month Rolling membership" is not.
	Lines before the first bullet are returned untouched in intro — they are not
	bullets, so nothing is done to them beyond passing them through.

	Only a *leading* marker is stripped: en dashes also appear inside the lines as
	range separators ("5:30am–3:30pm"), and a blanket replace would mangle those.

	Returns null when the text contains no bullets at all, so the caller can fall
	back to a plain paragraph rather than rendering prose as a single bullet.
 */
const descriptionBullet = /^[-\u2013]\s+/;

/*
	Most features are written as "label: value" ("Access: 5:30am-3:30pm"), and the
	label reads better in bold. Only the *first* colon splits the item, because the
	value itself is full of them ("5:30am").

	The label must be short and digit-free, which is what stops "Access 5:30am" —
	a feature that happens to contain a time but no label — from bolding "Access 5".
 */
const descriptionLabel = /^([^:\d]{1,30}):\s*/;

export type DescriptionItem = Readonly<{
	label: string|null,
	text: string,
}>;

function toDescriptionItem(item: string): DescriptionItem {
	const label = item.match(descriptionLabel);

	if (label === null) {
		return {label: null, text: item};
	}

	return {label: label[1].trim(), text: item.slice(label[0].length)};
}

export function parseDescriptionList(description: string): {intro: string[], items: DescriptionItem[]}|null {
	const lines = description.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
	const firstBullet = lines.findIndex((line) => descriptionBullet.test(line));

	if (firstBullet === -1) {
		return null;
	}

	const items: string[] = [];

	// A line after the first bullet that carries no marker of its own is a wrapped
	// continuation, not a new feature, so it joins the item above it.
	lines.slice(firstBullet).forEach((line) => {
		if (descriptionBullet.test(line)) {
			items.push(line.replace(descriptionBullet, ''));
		} else {
			items[items.length - 1] += ' ' + line;
		}
	});

	return {
		intro: lines.slice(0, firstBullet),
		items: items.map(toDescriptionItem),
	};
}
